import React from 'react';
import PropTypes from 'prop-types';

const Notification = ({ 
  message, 
  type = 'success', 
  onClose,
  autoClose = true,
  duration = 3000,
  action = null
}) => {
  // Close the notification after the specified duration
  React.useEffect(() => {
    if (autoClose && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      
      // Clean up the timer on unmount
      return () => {
        clearTimeout(timer);
      };
    }
  }, [autoClose, duration, onClose]);
  
  const getBackgroundAndBorderColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 text-green-800 border-l-4 border-green-500';
      case 'error':
        return 'bg-red-50 text-red-800 border-l-4 border-red-500';
      case 'warning':
        return 'bg-yellow-50 text-yellow-800 border-l-4 border-yellow-500';
      case 'info':
      default:
        return 'bg-blue-50 text-blue-800 border-l-4 border-blue-500';
    }
  };
  
  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
          </svg>
        );
    }
  };
  
  return (
    <div className={`flex items-center p-4 rounded-lg shadow-lg ${getBackgroundAndBorderColor()}`}>
      <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 mr-3 rounded-lg">
        {getIcon()}
      </div>
      <div className="text-sm font-medium">{message}</div>
      <div className="ml-auto flex space-x-2">
        {action && (
          <button 
            onClick={() => {
              if (action.onClick) action.onClick();
              if (onClose) onClose();
            }}
            className="text-xs bg-blue-500 text-white hover:bg-blue-600 rounded-lg px-3 py-1 inline-flex items-center justify-center"
            aria-label={action.text || "Action"}
          >
            {action.text === 'Undo' && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
            {action.text || "Action"}
          </button>
        )}
        {onClose && (
          <button 
            onClick={onClose}
            className="text-xs bg-white text-gray-600 hover:text-gray-900 rounded-lg px-2 py-1 inline-flex items-center justify-center border border-gray-200"
            aria-label="Hide notification"
          >
            {action ? "Dismiss" : "Close"}
          </button>
        )}
      </div>
    </div>
  );
};

Notification.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  onClose: PropTypes.func,
  autoClose: PropTypes.bool,
  duration: PropTypes.number,
  action: PropTypes.shape({
    text: PropTypes.string,
    onClick: PropTypes.func
  })
};

export default Notification;